import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { useActiveContext } from './useActiveContext';
import { config } from '../config';
import { User } from './useUser';

interface ApiErrorResponse {
  error: string;
  description?: string;
}

/**
 * Restores the caller's access to an organization they own but were detached
 * from.
 *
 * Reachable because organization membership comes from team membership while
 * ownership is a separate column: the last member of an org they own can remove
 * themselves from their only team, or delete it — permitted precisely because
 * there is no successor to hand the org to. They are then locked out of a tenant
 * they own, and the one-owned-org-per-user cap stops them starting over.
 *
 * The backend authorizes on ownership rather than a permission (they have none
 * there, by definition) and refuses if the organization still has members —
 * whoever is left can add them back, and that error message says so.
 *
 * Mirrors `useCreateOrganization`: the server picks the context to land in, so
 * pull the refreshed user and sync the local active-context state before
 * invalidating everything else.
 */
export function useRejoinOrganization() {
  const queryClient = useQueryClient();
  const { setActiveContextId } = useActiveContext();

  return useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    string
  >({
    mutationFn: async (organizationId: string) => {
      await axios.post(
        `${config.backendApi}/api/organizations/${organizationId}/rejoin`,
        {},
        { withCredentials: true },
      );
    },
    onSuccess: async () => {
      try {
        // Without this tab's context: `current` answers for the context a request names,
        // which is still the one the tab was in, and the server's choice is the remembered one.
        const { data: current } = await axios.get<User>(
          `${config.backendApi}/api/auth/current`,
          { withCredentials: true, headers: { 'X-Active-Context-Id': false } },
        );
        const contextId = current.activeContext?.id ?? current.lastActiveContextId ?? null;
        if (contextId) {
          setActiveContextId(contextId);
        }
      } catch {
        // Non-fatal: the invalidation below still refreshes the app into the
        // recovered organization even without a pre-set context.
      }
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      void queryClient.invalidateQueries();
    },
  });
}
